import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TestTube, Upload, Buildings, Sparkle } from "@phosphor-icons/react";

const STATUS_COLORS = {
  pending: "bg-terracotta text-white",
  under_review: "bg-slate text-white",
  reviewed: "bg-green-700 text-white",
  clarification_required: "bg-yellow-600 text-white",
};

export default function LabReports() {
  const [reports, setReports] = useState([]);
  const [labs, setLabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", lab_name: "", extracted_text: "", file_url: "" });
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(null);

  const load = () => Promise.all([
    api.get("/lab-reports/mine").then((r) => setReports(r.data)),
    api.get("/labs").then((r) => setLabs(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const runOcr = async (report) => {
    if (!report.file_url || !report.file_url.startsWith("data:image")) {
      toast.error("OCR only supports image uploads (JPG/PNG). This report has no image.");
      return;
    }
    setOcrLoading(report.id);
    try {
      await api.post(`/lab-reports/${report.id}/ocr`, { report_id: report.id, image_base64: report.file_url });
      toast.success("AI extracted the report — see summary below.");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "OCR failed");
    } finally { setOcrLoading(null); }
  };

  const submit = async () => {
    if (!form.title) { toast.error("Please give the report a title"); return; }
    setSaving(true);
    try {
      await api.post("/lab-reports", form);
      toast.success("Report uploaded — our medical team will review.");
      setOpen(false); setForm({ title: "", lab_name: "", extracted_text: "", file_url: "" });
      load();
    } catch { toast.error("Upload failed"); }
    finally { setSaving(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, file_url: reader.result }));
    reader.readAsDataURL(f);
  };

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline text-terracotta">Diagnostics & reports</div>
          <h1 className="font-heritage text-5xl text-ayurveda mt-1">Lab reports</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ayurveda hover:bg-ayurveda/90 text-bone h-11" data-testid="upload-report-btn">
              <Upload size={18} className="mr-2" /> Upload report
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-heritage text-3xl text-ayurveda">Upload a lab report</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Complete Blood Count" data-testid="report-title" /></div>
              <div><Label>Lab name</Label><Input value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} data-testid="report-lab" /></div>
              <div><Label>File (PDF or image)</Label><Input type="file" accept=".pdf,image/*" onChange={onFile} data-testid="report-file" /></div>
              <div><Label>Notes / extracted values (optional)</Label><Textarea value={form.extracted_text} onChange={(e) => setForm({ ...form, extracted_text: e.target.value })} data-testid="report-notes" /></div>
              <p className="text-xs text-charcoal/60">Our medical team reviews all uploads. Any AI-generated summary is marked and never replaces clinical review.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-ayurveda text-bone" data-testid="report-submit">{saving ? "Uploading…" : "Upload"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* My reports */}
      <div className="mb-10">
        <div className="overline text-charcoal/50 mb-3">My reports</div>
        {reports.length === 0 && (
          <div className="rounded-2xl bg-white p-10 text-center border border-black/5">
            <TestTube size={40} weight="duotone" className="mx-auto text-ayurveda/40 mb-3" />
            <div className="text-charcoal/60">No reports uploaded yet.</div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <Card key={r.id} className="border-black/5 bg-white" data-testid={`report-${r.id}`}>
              <CardContent className="p-5 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="font-clinical text-lg text-ayurveda">{r.title}</div>
                  <Badge className={STATUS_COLORS[r.review_status] || ""}>{r.review_status.replace("_"," ")}</Badge>
                </div>
                <div className="text-xs text-charcoal/60">Lab: {r.lab_name || "—"}</div>
                <div className="text-xs text-charcoal/60">Uploaded: {new Date(r.created_at).toLocaleDateString("en-IN")}</div>
                {r.file_url && r.file_url.startsWith("data:image") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runOcr(r)}
                    disabled={ocrLoading === r.id}
                    className="mt-2"
                    data-testid={`ocr-${r.id}`}
                  >
                    <Sparkle size={12} className="mr-1" /> {ocrLoading === r.id ? "Extracting…" : r.ai_summary ? "Re-run AI extract" : "AI extract with Claude"}
                  </Button>
                )}
                {r.ai_summary && (
                  <div className="mt-2 rounded-lg bg-bone p-3 text-sm text-charcoal/80 border border-terracotta/20">
                    <div className="overline text-terracotta text-[10px] mb-1 flex items-center gap-1"><Sparkle size={10} weight="fill" /> AI extract — pending clinical review</div>
                    <div className="whitespace-pre-wrap text-xs">{r.ai_summary}</div>
                  </div>
                )}
                {r.reviewer_notes && (
                  <div className="mt-2 rounded-lg bg-bone p-3 text-sm text-charcoal/80 border border-black/5">
                    <div className="overline text-terracotta text-[10px] mb-1">Reviewer note</div>
                    {r.reviewer_notes}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Verified labs */}
      <div>
        <div className="overline text-charcoal/50 mb-3">Verified diagnostic partners</div>
        <div className="grid md:grid-cols-3 gap-4">
          {labs.map((l) => (
            <Card key={l.id} className="border-black/5 bg-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Buildings size={18} weight="duotone" className="text-ayurveda" />
                  <div className="font-clinical text-ayurveda">{l.name}</div>
                </div>
                <div className="text-xs text-charcoal/60 mb-3">{l.city} · {l.phone}</div>
                <div className="flex flex-wrap gap-1">
                  {l.services.map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                </div>
                {l.home_collection && <div className="mt-3 text-xs text-green-800">✓ Home sample collection</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
